// Mock auth — replace with real API later (Laravel).
// Session is persisted in localStorage. No real password hashing — demo only.
import { useEffect, useState } from "react";

export type Role = "superadmin" | "org_owner" | "org_admin" | "org_member" | "client_portal";

export interface MockUser {
  id: string;
  email: string;
  password: string; // plain — mock only
  name: string;
  initials: string;
  role: Role;
  orgId: string | null; // null for superadmin
  clientId?: string; // for client_portal users
  avatarHue?: number;
}

export interface MockSession {
  userId: string;
  role: Role;
  orgId: string | null;
  clientId?: string;
  impersonating?: { originalUserId: string; orgId: string };
}

const SESSION_KEY = "mockSession";

export const mockUsers: MockUser[] = [
  // Superadmin
  { id: "u_admin", email: "admin@platform.test", password: "password", name: "Platform Admin", initials: "PA", role: "superadmin", orgId: null, avatarHue: 280 },

  // Thorne Studio (org_1)
  { id: "u_owner", email: "owner@thornestudio.test", password: "password", name: "Elias Thorne", initials: "ET", role: "org_owner", orgId: "org_1", avatarHue: 30 },
  { id: "u_editor", email: "editor@thornestudio.test", password: "password", name: "Mira Sato", initials: "MS", role: "org_member", orgId: "org_1", avatarHue: 200 },

  // Other studios (for admin views)
  { id: "u_owner2", email: "owner@lumenco.test", password: "password", name: "Ana Lumière", initials: "AL", role: "org_owner", orgId: "org_2", avatarHue: 120 },
  { id: "u_owner3", email: "owner@northlight.test", password: "password", name: "Owen Park", initials: "OP", role: "org_owner", orgId: "org_3", avatarHue: 200 },
  { id: "u_owner4", email: "owner@verdant.test", password: "password", name: "Sara Vidal", initials: "SV", role: "org_owner", orgId: "org_4", avatarHue: 90 },

  // Client portal users (linked to clients in Thorne Studio)
  { id: "u_client_jane", email: "jane@client.test", password: "password", name: "Marcus Halden", initials: "MH", role: "client_portal", orgId: "org_1", clientId: "c1", avatarHue: 15 },
  { id: "u_client_sia", email: "sia@client.test", password: "password", name: "Sia Agency", initials: "SA", role: "client_portal", orgId: "org_1", clientId: "c2", avatarHue: 320 },
  { id: "u_client_vogue", email: "vogue@client.test", password: "password", name: "VOGUE Japan", initials: "VJ", role: "client_portal", orgId: "org_1", clientId: "c4", avatarHue: 0 },
];

export function getSession(): MockSession | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as MockSession) : null;
  } catch {
    return null;
  }
}

export function setSession(s: MockSession | null) {
  if (typeof window === "undefined") return;
  if (s === null) window.localStorage.removeItem(SESSION_KEY);
  else window.localStorage.setItem(SESSION_KEY, JSON.stringify(s));
  // notify subscribers in this tab
  window.dispatchEvent(new Event("mock-auth-change"));
}

export function getUser(id: string | null | undefined): MockUser | null {
  if (!id) return null;
  return mockUsers.find((u) => u.id === id) ?? null;
}

export function signIn(email: string, password: string, expectedRoles?: Role[]): { ok: true; user: MockUser } | { ok: false; error: string } {
  const u = mockUsers.find((x) => x.email.toLowerCase() === email.toLowerCase().trim());
  if (!u || u.password !== password) return { ok: false, error: "Invalid email or password." };
  if (expectedRoles && !expectedRoles.includes(u.role)) {
    return { ok: false, error: "This account doesn't have access to this area." };
  }
  setSession({ userId: u.id, role: u.role, orgId: u.orgId, clientId: u.clientId });
  return { ok: true, user: u };
}

export function signOut() {
  setSession(null);
}

export function impersonate(orgOwnerUserId: string) {
  const me = getSession();
  if (!me || me.role !== "superadmin") return;
  const target = getUser(orgOwnerUserId);
  if (!target || !target.orgId) return;
  setSession({
    userId: target.id,
    role: target.role,
    orgId: target.orgId,
    impersonating: { originalUserId: me.userId, orgId: target.orgId },
  });
}

export function exitImpersonation() {
  const me = getSession();
  if (!me?.impersonating) return;
  const original = getUser(me.impersonating.originalUserId);
  if (!original) {
    setSession(null);
    return;
  }
  setSession({ userId: original.id, role: original.role, orgId: original.orgId });
}

export function useMockAuth() {
  const [session, setSessionState] = useState<MockSession | null>(null);
  const [user, setUser] = useState<MockUser | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const sync = () => {
      const s = getSession();
      setSessionState(s);
      setUser(getUser(s?.userId));
      setReady(true);
    };
    sync();
    window.addEventListener("mock-auth-change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("mock-auth-change", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return { session, user, ready };
}

export function homeForRole(role: Role): string {
  switch (role) {
    case "superadmin": return "/admin";
    case "client_portal": return "/portal";
    default: return "/";
  }
}

export function loginForRole(role: Role): string {
  switch (role) {
    case "superadmin": return "/admin/login";
    case "client_portal": return "/portal/login";
    default: return "/auth";
  }
}
